import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { inr, apiError } from "../../lib/api";
import { PageHeader, Card, Loading, Empty, StatusBadge, inputCls, btnSecondary } from "../../components/admin/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Search, Filter, X, Trash2, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { v: "", l: "All" },
  { v: "pending", l: "Pending" },
  { v: "pending_noproof", l: "Pending · No Proof" },
  { v: "pending_submitted", l: "Pending · Proof Submitted" },
  { v: "completed", l: "Completed" },
  { v: "rejected", l: "Rejected" },
  { v: "duplicate", l: "Duplicate" },
  { v: "waitlist", l: "Waitlist (Batch Full)" },
];
const SEARCH_FIELDS = [["name", "Student Name"], ["email", "Email ID"], ["transaction", "Transaction ID"]];
const PLACEHOLDER = { name: "Search student name...", email: "Search email ID...", transaction: "Search transaction ID..." };
const FKEY = "gdiot_pay_reg_filters";
export const REG_ORDER_KEY = "gdiot_pay_reg_order";
const readSaved = () => { try { return JSON.parse(localStorage.getItem(FKEY) || "{}"); } catch { return {}; } };

const tabCls = (active) =>
  `px-4 py-2 rounded-xl text-sm font-semibold capitalize transition-all ${
    active ? "bg-blue-600 text-white shadow-md shadow-blue-600/20" : "bg-white border border-slate-200 text-slate-600 hover:border-blue-300"
  }`;

function primaryOf(r) {
  const s = r.payment_status;
  if (s === "verified") return "completed";
  if (s === "rejected") return "rejected";
  if (s === "duplicate") return "duplicate";
  return "pending";
}

function WaitlistBar({ filters, count }) {
  const [busy, setBusy] = useState("");
  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const params = { fmt, waitlist: true, ...(filters.school_id && { school_id: filters.school_id }), ...(filters.program_id && { program_id: filters.program_id }) };
      const res = await api.get("/export/registrations", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `waitlist_batch_full.${fmt === "xlsx" ? "xlsx" : "csv"}`; a.click();
      URL.revokeObjectURL(url); toast.success("Waitlist downloaded");
    } catch (e) { toast.error("Download failed"); }
    setBusy("");
  };
  return (
    <Card className="p-4 mb-4 flex flex-wrap items-center gap-3 border-amber-200 bg-amber-50/50" data-testid="waitlist-bar">
      <div className="flex-1 min-w-[220px] text-sm text-amber-900">
        <b>{count ?? "…"}</b> parents filled the form while the batch was full (paused/closed) and have not paid yet. Use the filters to narrow by school or program.
      </div>
      {["xlsx", "csv"].map((fmt) => (
        <button key={fmt} className={btnSecondary} onClick={() => download(fmt)} disabled={!!busy} data-testid={`waitlist-download-${fmt}`}>
          {busy === fmt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {fmt === "xlsx" ? "Excel" : "CSV"}
        </button>
      ))}
    </Card>
  );
}

export default function Registrations() {
  const saved = readSaved();
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState(saved.search || "");
  const [searchBy, setSearchBy] = useState(saved.searchBy || "name");
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({
    school_id: saved.school_id || "",
    program_id: saved.program_id || "",
    primary: saved.primary != null ? saved.primary : (params.get("primary") || ""),
  });
  const [masters, setMasters] = useState({ schools: [], programs: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allIds = (rows || []).map((r) => r.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));
  const toggleAll = () => setSelected(allSelected ? [] : allIds);

  const bulkDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/registrations/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} registration${data.deleted === 1 ? "" : "s"} moved to Bin`);
      setSelected([]); setConfirmDelete(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setDeleting(false);
  };

  useEffect(() => {
    Promise.all([api.get("/schools"), api.get("/programs")]).then(([s, p]) => setMasters({ schools: s.data, programs: p.data }));
  }, []);

  const load = useCallback(() => {
    const qp = new URLSearchParams();
    if (search) { qp.set("search", search); qp.set("search_by", searchBy); }
    if (filters.school_id) qp.set("school_id", filters.school_id);
    if (filters.program_id) qp.set("program_id", filters.program_id);
    if (filters.primary) qp.set("primary", filters.primary);
    try { localStorage.setItem(FKEY, JSON.stringify({ search, searchBy, ...filters })); } catch { /* ignore */ }
    setRows(null);
    api.get(`/registrations?${qp}`).then((r) => {
      setRows(r.data);
      try { localStorage.setItem(REG_ORDER_KEY, JSON.stringify(r.data.map((x) => x.id))); } catch { /* ignore */ }
    }).catch(() => setRows([]));
  }, [search, searchBy, filters]);

  useEffect(() => { setSelected([]); }, [search, searchBy, filters]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const setPrimary = (v) => {
    setFilters((f) => ({ ...f, primary: v }));
    const np = new URLSearchParams(params);
    if (v) np.set("primary", v); else np.delete("primary");
    setParams(np, { replace: true });
  };
  const resetFilters = () => { setFilters({ school_id: "", program_id: "", primary: "" }); setParams({}, { replace: true }); };

  return (
    <div>
      <PageHeader title="Payment" subtitle="All registrations and their payments in one place." />

      <div className="flex flex-wrap gap-2 mb-4" data-testid="payment-status-tabs">
        {TABS.map((t) => (
          <button key={t.v || "all"} onClick={() => setPrimary(t.v)} data-testid={`tab-${t.v || "all"}`} className={tabCls(filters.primary === t.v)}>{t.l}</button>
        ))}
      </div>

      {filters.primary === "waitlist" && <WaitlistBar filters={filters} count={rows?.length} />}

      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input className={`${inputCls} pl-10 pr-9`} placeholder={PLACEHOLDER[searchBy]} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="reg-search" />
            {search && <button onClick={() => setSearch("")} data-testid="reg-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
          </div>
          <select className={`${inputCls} sm:w-48`} value={searchBy} onChange={(e) => setSearchBy(e.target.value)} data-testid="reg-search-by" aria-label="Search By">
            {SEARCH_FIELDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button className={btnSecondary} onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters"><Filter className="w-4 h-4" /> Filters</button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-100">
            <select className={inputCls} value={filters.school_id} onChange={(e) => setFilters({ ...filters, school_id: e.target.value })}><option value="">All Schools</option>{masters.schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
            <select className={inputCls} value={filters.program_id} onChange={(e) => setFilters({ ...filters, program_id: e.target.value })}><option value="">All Programs</option>{masters.programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
            <button onClick={resetFilters} className={btnSecondary}><X className="w-4 h-4" /> Reset</button>
          </div>
        )}
      </Card>

      <Card>
        {rows === null ? <Loading /> : rows.length === 0 ? <Empty label="No results found — no records match your search and filters." /> : (
          <div className="overflow-x-auto">
            <div className="px-5 pt-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-slate-600" data-testid="reg-count"><span className="font-head font-bold text-slate-900">{rows.length}</span> {rows.length === 1 ? "result" : "results"} found</div>
              {selected.length > 0 && (
                <div className="flex items-center gap-3" data-testid="bulk-action-bar">
                  <span className="text-sm text-slate-500">{selected.length} selected</span>
                  <button onClick={() => setConfirmDelete(true)} data-testid="bulk-delete-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700"><Trash2 className="w-4 h-4" /> Delete Selected</button>
                </div>
              )}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded border-slate-300 text-blue-600 cursor-pointer" data-testid="select-all" aria-label="Select all" /></th>
                  {["ID", "Student", "Parent / Mobile", "School / Program", "Required", "Paid", "Remaining", "Payments", "Status"].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={`border-b border-slate-50 hover:bg-slate-50/60 ${selected.includes(r.id) ? "bg-blue-50/40" : ""}`} data-testid="reg-row">
                    <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleOne(r.id)} className="rounded border-slate-300 text-blue-600 cursor-pointer" data-testid={`select-${r.id}`} aria-label={`Select ${r.registration_id}`} />
                    </td>
                    <td className="px-5 py-3.5 font-mono text-xs text-blue-600 font-semibold cursor-pointer" onClick={() => navigate(`/admin/registrations/${r.id}`)}>{r.registration_id}</td>
                    <td className="px-5 py-3.5 font-medium text-slate-800 cursor-pointer" onClick={() => navigate(`/admin/registrations/${r.id}`)}>{r.student_name}<div className="text-xs text-slate-400 font-normal">{r.level_name} {r.batch_name}</div></td>
                    <td className="px-5 py-3.5 text-slate-600 cursor-pointer" onClick={() => navigate(`/admin/registrations/${r.id}`)}>{r.parent_name}<div className="text-xs text-slate-400 font-mono">{r.mobile}</div></td>
                    <td className="px-5 py-3.5 text-slate-600 text-xs cursor-pointer" onClick={() => navigate(`/admin/registrations/${r.id}`)}>{r.school_name || "—"}<div className="text-slate-400">{r.program_name}</div></td>
                    <td className="px-5 py-3.5 font-medium text-slate-800 cursor-pointer" onClick={() => navigate(`/admin/registrations/${r.id}`)}>{inr(r.amount)}</td>
                    <td className="px-5 py-3.5 text-emerald-700 font-medium cursor-pointer" onClick={() => navigate(`/admin/registrations/${r.id}`)}>{inr(r.total_paid || 0)}</td>
                    <td className="px-5 py-3.5 text-slate-600 cursor-pointer" onClick={() => navigate(`/admin/registrations/${r.id}`)}>{inr(r.remaining != null ? r.remaining : r.amount)}</td>
                    <td className="px-5 py-3.5 text-center cursor-pointer" onClick={() => navigate(`/admin/registrations/${r.id}`)}>{r.num_payments || 0}</td>
                    <td className="px-5 py-3.5 cursor-pointer" onClick={() => navigate(`/admin/registrations/${r.id}`)}><StatusBadge status={r.primary_status || primaryOf(r)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(false)}>
        <DialogContent data-testid="bulk-delete-dialog">
          <DialogHeader><DialogTitle>Delete {selected.length} registration{selected.length === 1 ? "" : "s"}?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">The selected registrations and all their data (student details and payments) will be moved to the <span className="font-semibold">Bin</span>. They will be kept for <span className="font-semibold">10 days</span> so you can restore them, then permanently deleted. Binned records are excluded from all lists, collection and revenue.</p>
          <DialogFooter>
            <button onClick={() => setConfirmDelete(false)} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 font-medium">Cancel</button>
            <button onClick={bulkDelete} disabled={deleting} data-testid="confirm-bulk-delete-btn" className="px-4 py-2 rounded-lg bg-rose-600 text-white font-semibold flex items-center gap-2 disabled:opacity-60">{deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Move to Bin</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
